/**
 * AttributionOptionsPageV2 — redesigned port of AttributionOptionsPage.
 *
 * Page layout:
 *   • PageHeader with "+ Add Option" coral action.
 *   • Rollout percentage card — three per-platform integer inputs +
 *     a Save button. Each input writes its own
 *     `user_attribution_percentage_<os>` general_info key. APIv1 falls
 *     back to the legacy single-value key when a per-platform key is
 *     missing, so old data shows up until each platform is saved.
 *   • Three side-by-side LibTables — iOS / Android / Web App options.
 *     Each row has hover-revealed Edit + Delete actions.
 *   • Modal for create / edit (Value text + OS select).
 *
 * Same Redux + apiV2 plumbing as v1: `fetchAttributionOptions`,
 * `createAttributionOption`, `updateAttributionOption`,
 * `deleteAttributionOption`, `fetchGeneralInfo`, `updateGeneralInfo`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  OS_ANDROID,
  OS_IOS,
  OS_LABELS,
  OS_WEB_APP,
  createAttributionOption,
  deleteAttributionOption,
  fetchAttributionOptions,
  updateAttributionOption,
} from '../../../../actions/attributionOptionsActions';
import {
  fetchGeneralInfo,
  updateGeneralInfo,
} from '../../../../actions/generalInfoActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  PageHeader,
  Button,
  Modal,
  LibTable,
  IconButton,
  cn,
} from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  TextInput,
  Select,
} from '../../../redesign/edit';

/* ─── Constants ────────────────────────────────────────────────────── */

const LEGACY_ROLLOUT_KEY = 'user_attribution_percentage';
const ROLLOUT_KEYS = {
  [OS_IOS]:     'user_attribution_percentage_ios',
  [OS_ANDROID]: 'user_attribution_percentage_android',
  [OS_WEB_APP]: 'user_attribution_percentage_webapp',
};
const PLATFORM_ORDER = [OS_IOS, OS_ANDROID, OS_WEB_APP];

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function validatePercentage(raw) {
  if (raw === '' || raw === null || raw === undefined) return 'Required';
  if (!/^\d+$/.test(String(raw))) return 'Integer 0 - 100';
  const n = parseInt(raw, 10);
  if (n < 0 || n > 100) return '0 - 100';
  return '';
}

/* ─── Per-platform options table ──────────────────────────────────── */

function OptionsTable({ title, options, onEdit, onDelete }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 11, letterSpacing: '0.08em', padding: '6px 4px 8px' }}
      >
        {title} <span className="text-navy">({options.length})</span>
      </div>
      {options.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 12, padding: '24px 16px', fontSize: 12.5 }}
        >
          No options yet.
        </div>
      ) : (
        <LibTable
          fixed
          className="border border-sand"
          style={{ borderRadius: 12, overflow: 'hidden' }}
        >
          <colgroup>
            <col style={{ width: 60 }} />
            <col />
            <col style={{ width: 90 }} />
          </colgroup>
          <LibTable.Head>
            <tr>
              <LibTable.Th>ID</LibTable.Th>
              <LibTable.Th>Value</LibTable.Th>
              <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
            </tr>
          </LibTable.Head>
          <tbody>
            {options.map((o) => (
              <LibTable.Row key={o.id}>
                <LibTable.Td>
                  <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
                    {o.id}
                  </span>
                </LibTable.Td>
                <LibTable.Td>
                  <span className="text-navy" style={{ fontSize: 13.5 }}>
                    {o.value}
                  </span>
                </LibTable.Td>
                <LibTable.Td style={{ textAlign: 'right' }}>
                  <div
                    className={cn(
                      'inline-flex opacity-0 transition-opacity duration-150',
                      'group-hover:opacity-100',
                    )}
                    style={{ gap: 4 }}
                  >
                    <IconButton size="sm" aria-label="Edit" title="Edit" onClick={() => onEdit(o)}>
                      {EditIconSvg}
                    </IconButton>
                    <IconButton size="sm" aria-label="Delete" title="Delete" onClick={() => onDelete(o)}>
                      {TrashIconSvg}
                    </IconButton>
                  </div>
                </LibTable.Td>
              </LibTable.Row>
            ))}
          </tbody>
        </LibTable>
      )}
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function AttributionOptionsPageV2() {
  const dispatch = useDispatch();

  const attributionState = useSelector((s) => s.attribution_options);
  const generalInfo = useSelector((s) => s.general_info?.general_info || []);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogValue, setDialogValue] = useState('');
  const [dialogOs, setDialogOs] = useState(OS_IOS);
  const [saving, setSaving] = useState(false);

  const [rolloutInputs, setRolloutInputs] = useState({
    [OS_IOS]: '',
    [OS_ANDROID]: '',
    [OS_WEB_APP]: '',
  });
  const [rolloutErrors, setRolloutErrors] = useState({
    [OS_IOS]: '',
    [OS_ANDROID]: '',
    [OS_WEB_APP]: '',
  });
  const [savingRollout, setSavingRollout] = useState(false);

  useEffect(() => {
    dispatch(fetchAttributionOptions());
    dispatch(fetchGeneralInfo());
  }, [dispatch]);

  // Sync inputs from general_info, falling back to the legacy single-value
  // key when a per-platform key is missing. Guarded on `savingRollout` so
  // the post-update reducer flip doesn't transiently revert inputs while
  // the follow-up fetchGeneralInfo is in flight.
  useEffect(() => {
    if (savingRollout) return;
    const lookup = (key) =>
      generalInfo.find((row) => row.general_info_key === key);
    const legacy = lookup(LEGACY_ROLLOUT_KEY);
    const legacyValue = legacy?.general_info_value;

    let changed = false;
    const next = { ...rolloutInputs };
    PLATFORM_ORDER.forEach((os) => {
      const row = lookup(ROLLOUT_KEYS[os]);
      let value;
      if (row?.general_info_value !== undefined && row?.general_info_value !== null) {
        value = String(row.general_info_value);
      } else if (legacyValue !== undefined && legacyValue !== null) {
        value = String(legacyValue);
      } else {
        value = '';
      }
      if (next[os] !== value) {
        next[os] = value;
        changed = true;
      }
    });
    if (changed) {
      setRolloutInputs(next);
      setRolloutErrors({
        [OS_IOS]: '', [OS_ANDROID]: '', [OS_WEB_APP]: '',
      });
    }
  }, [generalInfo, savingRollout]);

  const iosOptions = useMemo(
    () => [...(attributionState?.iOS || [])].sort((a, b) => a.id - b.id),
    [attributionState?.iOS],
  );
  const androidOptions = useMemo(
    () => [...(attributionState?.android || [])].sort((a, b) => a.id - b.id),
    [attributionState?.android],
  );
  const webAppOptions = useMemo(
    () => [...(attributionState?.webApp || [])].sort((a, b) => a.id - b.id),
    [attributionState?.webApp],
  );

  const openCreateDialog = () => {
    setEditing(null);
    setDialogValue('');
    setDialogOs(OS_IOS);
    setDialogOpen(true);
  };

  const openEditDialog = (o) => {
    setEditing(o);
    setDialogValue(o.value || '');
    setDialogOs(o.os);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleDialogSave = async () => {
    const trimmed = dialogValue.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      if (editing) {
        await dispatch(updateAttributionOption(editing.id, { value: trimmed, os: dialogOs }));
      } else {
        await dispatch(createAttributionOption({ value: trimmed, os: dialogOs }));
      }
      await dispatch(fetchAttributionOptions());
      setDialogOpen(false);
    } catch {
      // errorHandler surfaces a Swal already
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (o) => {
    const result = await Swal.fire({
      title: 'Delete attribution option?',
      text: `"${o.value}" (${OS_LABELS[o.os] || 'OS'}) will be removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await dispatch(deleteAttributionOption(o.id, o.os));
      await dispatch(fetchAttributionOptions());
    } catch {
      /* handled via Swal */
    }
  };

  const handleRolloutChange = (os) => (e) => {
    const v = e.target.value;
    setRolloutInputs((prev) => ({ ...prev, [os]: v }));
    setRolloutErrors((prev) => ({ ...prev, [os]: validatePercentage(v) }));
  };

  const hasAnyRolloutError = PLATFORM_ORDER.some((os) => !!rolloutErrors[os]);

  const handleRolloutSave = async () => {
    const errors = {};
    PLATFORM_ORDER.forEach((os) => {
      errors[os] = validatePercentage(rolloutInputs[os]);
    });
    if (Object.values(errors).some(Boolean)) {
      setRolloutErrors(errors);
      return;
    }
    setSavingRollout(true);
    try {
      for (const os of PLATFORM_ORDER) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(
          updateGeneralInfo({
            general_info_key: ROLLOUT_KEYS[os],
            general_info_value: String(parseInt(rolloutInputs[os], 10)),
          }),
        );
      }
      Swal.fire({
        icon: 'success',
        title: 'Rollout percentages saved',
        timer: 1500,
        showConfirmButton: false,
      });
      await dispatch(fetchGeneralInfo());
    } catch {
      // errorHandler already surfaces a Swal
    } finally {
      setSavingRollout(false);
    }
  };

  const loading = !!attributionState?.loading;

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={loading} />
      <PageHeader
        eyebrow="Engagement & Promotions"
        title="Attribution Options"
        actions={
          <Button variant="coral" leadingIcon={PlusSign} onClick={openCreateDialog}>
            Add Option
          </Button>
        }
      />

      <div style={{ padding: '0 28px 60px' }}>
        {/* Rollout percentage card — single FormSection with three
            inline number inputs + Save. */}
        <FormSection
          title="Rollout percentage"
          num="01"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              general_info · user_attribution_percentage_*
            </span>
          }
        >
          <p
            className="text-text-muted"
            style={{ fontSize: 12.5, margin: '0 0 12px' }}
          >
            Percentage of eligible new users who see the attribution
            screen on each platform. Each value is stored as its own
            <code className="font-mono"> user_attribution_percentage_*</code>{' '}
            key. APIv1 falls back to the legacy single-value key when a
            per-platform key is missing.
          </p>
          {/* Three compact 0-100 inputs side-by-side on one row.
           *  Flex (not FieldRow's grid) so each cell stays at its
           *  compact 120-wide intrinsic size — these only hold 0-100
           *  and don't need a 1fr share of the section. */}
          <div
            className="flex items-start flex-wrap"
            style={{ gap: 14 }}
          >
            {PLATFORM_ORDER.map((os) => (
              <div key={os} style={{ width: 120, flexShrink: 0 }}>
                <FieldBlock
                  label={OS_LABELS[os]}
                  hint={rolloutErrors[os] || 'Integer 0 – 100'}
                  htmlFor={`f-rollout-${os}`}
                  noMargin
                >
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <TextInput
                      id={`f-rollout-${os}`}
                      value={rolloutInputs[os]}
                      onChange={handleRolloutChange(os)}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      hasError={!!rolloutErrors[os]}
                      style={{ paddingRight: 28 }}
                    />
                    <span
                      aria-hidden
                      className="text-text-muted"
                      style={{ position: 'absolute', right: 12, fontSize: 13 }}
                    >
                      %
                    </span>
                  </div>
                </FieldBlock>
              </div>
            ))}
          </div>
          <div
            className="flex items-center"
            style={{ gap: 12, marginTop: 12 }}
          >
            <Button
              variant="coral"
              leadingIcon={SaveIconSvg}
              onClick={handleRolloutSave}
              disabled={hasAnyRolloutError || savingRollout}
              type="button"
            >
              {savingRollout ? 'Saving…' : 'Save rollout %'}
            </Button>
            {hasAnyRolloutError && (
              <span className="text-coral" style={{ fontSize: 12 }}>
                Fix the highlighted inputs to save.
              </span>
            )}
          </div>
        </FormSection>

        {/* Three OS option tables side-by-side on wide, stacked on narrow. */}
        <div
          className="grid grid-cols-1 md:grid-cols-3"
          style={{ gap: 20, marginTop: 8 }}
        >
          <OptionsTable
            title="iOS"
            options={iosOptions}
            onEdit={openEditDialog}
            onDelete={handleDelete}
          />
          <OptionsTable
            title="Android"
            options={androidOptions}
            onEdit={openEditDialog}
            onDelete={handleDelete}
          />
          <OptionsTable
            title="Web App"
            options={webAppOptions}
            onEdit={openEditDialog}
            onDelete={handleDelete}
          />
        </div>
      </div>

      <Modal
        open={dialogOpen}
        onClose={closeDialog}
        eyebrow="Attribution Options"
        title={editing ? 'Edit option' : 'New option'}
        maxWidth={520}
        footer={
          <>
            <Button variant="ghost" onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="coral"
              onClick={handleDialogSave}
              disabled={saving || !dialogValue.trim()}
            >
              {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FieldBlock label="Value" required htmlFor="f-attr-value" noMargin>
            <TextInput
              id="f-attr-value"
              value={dialogValue}
              onChange={(e) => setDialogValue(e.target.value)}
              autoFocus
              placeholder="e.g. Podcast"
            />
          </FieldBlock>
          <FieldBlock label="OS" htmlFor="f-attr-os" noMargin>
            <Select
              id="f-attr-os"
              value={dialogOs}
              onChange={(e) => setDialogOs(Number(e.target.value))}
            >
              <option value={OS_IOS}>iOS</option>
              <option value={OS_ANDROID}>Android</option>
              <option value={OS_WEB_APP}>Web App</option>
            </Select>
          </FieldBlock>
        </div>
      </Modal>
    </div>
  );
}
